import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        // Future Station Variants
        "fs-primary": "bg-gradient-to-r from-fs-purple-500 to-fs-cyan-500 text-white font-medium shadow-fs-glow hover:shadow-fs-hover hover:scale-105 active:scale-95",
        "fs-secondary": "border-2 border-fs-purple-500 text-fs-purple-500 hover:bg-fs-purple-500 hover:text-white bg-transparent backdrop-blur-sm",
        "fs-whatsapp": "bg-[#25D366] text-white hover:bg-[#25D366]/90 shadow-lg hover:shadow-xl hover:scale-105 active:scale-95",
        "fs-outline": "border border-fs-purple-500/30 text-foreground dark:text-white hover:border-fs-purple-500 hover:bg-fs-purple-500/10 bg-transparent backdrop-blur-sm",
        "fs-ghost": "text-foreground dark:text-white hover:bg-fs-purple-500/20 hover:text-fs-purple-200",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-12 rounded-xl px-8 text-base",
        xl: "h-14 rounded-xl px-10 text-lg",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
